import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { CTA, Footer } from './App';
import './DonatePage.css';

/* ============================================
   SCROLL ANIMATION HOOK
   ============================================ */
function useScrollAnimation() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const current = ref.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = '', delay = 0, direction = 'up', immediate = false }) {
  const [ref, isVisible] = useScrollAnimation();
  const dirClass = direction === 'left' ? 'fade-in-left' : direction === 'right' ? 'fade-in-right' : 'fade-in';
  return (
    <div ref={ref} className={`${dirClass} ${immediate || isVisible ? 'visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

/* ============================================
   ICONS
   ============================================ */
const Icons = {
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
    </svg>
  ),
  Heart: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
    </svg>
  ),
  CreditCard: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>
    </svg>
  ),
  Shield: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
    </svg>
  ),
  Lightbulb: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/>
    </svg>
  ),
};

/* ============================================
   DONATION AMOUNTS
   ============================================ */
const donationAmounts = [10, 25, 50, 100, 250, 500];

/* ============================================
   DONATE PAGE
   ============================================ */
function DonatePage() {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleAmountClick = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setCustomAmount(val);
    setIsCustom(true);
    if (val) setSelectedAmount(parseInt(val, 10));
  };

  const finalAmount = isCustom ? (parseInt(customAmount, 10) || 0) : selectedAmount;

  return (
    <div className="donate-page">
      <Helmet>
        <title>Donate to Mission Critical Support Group | Tax-Deductible 501(c)(3) - Katy, TX</title>
        <meta name="description" content="Make a tax-deductible donation to Mission Critical Support Group, a 501(c)(3) nonprofit in Katy, TX. Your contribution provides housing, meals, education, and advocacy for homeless veterans and survivors of domestic violence. EIN: 39-3059429." />
        <link rel="canonical" href="https://www.missioncriticalsupportgroup.com/donate" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.missioncriticalsupportgroup.com/donate" />
        <meta property="og:title" content="Donate to Mission Critical Support Group | Support Veterans & Survivors" />
        <meta property="og:description" content="Your tax-deductible donation provides housing, meals, education, and advocacy for veterans and survivors of domestic violence in Katy, TX. 501(c)(3) EIN: 39-3059429." />
        <meta property="og:site_name" content="Mission Critical Support Group" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Donate to Mission Critical Support Group" />
        <meta name="twitter:description" content="Tax-deductible donations provide housing, meals, and education for veterans and domestic violence survivors in Katy, TX." />
      </Helmet>

      {/* Navigation */}
      <nav className="donate-nav">
        <div className="donate-nav-inner">
          <a href="/" className="donate-nav-back"><Icons.ArrowLeft /><span>Back to Home</span></a>
        </div>
      </nav>

      {/* Hero */}
      <section className="donate-hero">
        <AnimatedSection immediate>
          <div className="donate-hero-label">Support Our Mission</div>
          <h1 className="donate-hero-title">Every Contribution Restores Hope.</h1>
          <p className="donate-hero-description">
            Your generosity helps Mission Critical Support Group provide housing, meals, education,
            and advocacy for homeless veterans and survivors of domestic violence in Katy, TX and
            Fort Bend County. Every tax-deductible dollar fuels programs that restore dignity and rebuild lives.
          </p>
        </AnimatedSection>
      </section>

      {/* Donation Section */}
      <section className="donate-paypal-section">
        <div className="donate-paypal-inner">
          <AnimatedSection>
            <div className="donate-paypal-card">
              <div className="donate-paypal-header">
                <div className="donate-paypal-icon"><Icons.CreditCard /></div>
                <div>
                  <h2>Make a Donation</h2>
                  <p className="donate-paypal-subtitle">Secure and tax-deductible</p>
                </div>
              </div>

              <div className="donate-amounts">
                {donationAmounts.map((amount) => (
                  <button key={amount} className={`donate-amount-btn ${!isCustom && selectedAmount === amount ? 'active' : ''}`} onClick={() => handleAmountClick(amount)}>
                    ${amount}
                  </button>
                ))}
                <div className={`donate-amount-custom ${isCustom ? 'active' : ''}`}>
                  <span className="donate-custom-dollar">$</span>
                  <input type="text" placeholder="Other" value={customAmount} onChange={handleCustomChange} onFocus={() => setIsCustom(true)} />
                </div>
              </div>

              <button
                className="donate-paypal-btn"
                style={{ pointerEvents: finalAmount <= 0 ? 'none' : 'auto', opacity: finalAmount <= 0 ? 0.5 : 1 }}
                onClick={() => { if (finalAmount > 0) { window.open(`https://www.paypal.com/donate/?hosted_button_id=NHZ39Q9EZQRQY&amount=${finalAmount}`, '_blank'); } }}
              >
                <Icons.Heart />
                <span>Donate ${finalAmount > 0 ? finalAmount : '\u2014'}</span>
                <Icons.ArrowRight />
              </button>

              <div className="donate-paypal-security">
                <Icons.Shield />
                <span>256-bit SSL encrypted &bull; Tax-deductible &bull; Secure payment</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Zelle Section */}
      <section className="donate-zelle-section">
        <div className="donate-zelle-inner">
          <AnimatedSection direction="left">
            <div className="donate-zelle-text">
              <div className="donate-zelle-label">Directly Support Mission Critical Support Group</div>
              <h2 className="donate-zelle-title">Donate via Zelle</h2>
              <p className="donate-zelle-description">
                Your contribution helps us provide housing, meals, education, and advocacy
                for homeless veterans and survivors of domestic violence. Every donation makes
                a real impact. Help us avoid payment processor fees by donating directly through Zelle.
              </p>
              <a href="#zelle-qr" className="donate-zelle-cta">
                Scan the QR code <Icons.ArrowRight />
              </a>
            </div>
          </AnimatedSection>
          <AnimatedSection direction="right">
            <div className="donate-zelle-qr-card" id="zelle-qr">
              <img src="/images/zelle-qr.webp" alt="Zelle QR code to donate to Mission Critical Support Group" className="donate-zelle-qr-img" />
              <p className="donate-zelle-qr-caption">Scan with your banking app to donate via Zelle</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Photo + Message Section */}
      <section className="donate-photo-section">
        <div className="donate-photo-inner">
          <AnimatedSection>
            <div className="donate-photo-card">
              <img src="/images/donate-hero.jpg" alt="Military family reunion, a veteran returning home" className="donate-photo-img" />
              <div className="donate-photo-text">
                <p>
                  Every contribution, no matter the size, helps us provide housing, meals, education,
                  and advocacy services to veterans and survivors of domestic violence in Katy, Fulshear,
                  and the greater Houston area. Together, we are building a community where everyone
                  has the support they need to thrive. Thank you for being part of the mission.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Tax Note */}
      <section className="donate-tax-section">
        <div className="donate-tax-inner">
          <AnimatedSection>
            <div className="donate-tax-card">
              <Icons.Lightbulb />
              <p>
                Mission Critical Support Group is a registered <strong>501(c)(3) nonprofit organization</strong> (EIN: 39-3059429).
                All donations are tax-deductible to the extent allowed by law.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}

export default DonatePage;
